import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, Fingerprint, ShieldCheck } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { setNavigationGuard, navigate } from '../router.js'
import { useOverlay } from './overlays.js'
import { usePresence } from './presence.js'
import { setStepUpRequester } from './stepup-bus.js'
import { apiMutate, useToast } from '../admin/primitives.js'

/**
 * ÉDITION & SAVE BAR GLOBALE (LOT 5.5.1) — la règle produit LIVE CONTROL :
 *
 *     DANS GESTION, ON NE CHERCHE JAMAIS LE BOUTON « ENREGISTRER ».
 *
 * On modifie quelque chose ; Gestion détecte l'écart avec l'état serveur ;
 * la barre flottante apparaît en bas ; elle propose Annuler / Enregistrer ;
 * elle disparaît quand l'état est de nouveau synchronisé.
 *
 * Architecture — UNE unité d'édition logique par route ou par modale :
 *   · la page appelle `useSaveBar({...})` avec son état dirty RÉEL (diff
 *     avec le snapshot serveur, jamais un simple focus/ouverture de menu) ;
 *   · le provider affiche la barre, gère Ctrl/Cmd+S, `beforeunload`, la
 *     garde de navigation (modale « Modifications non enregistrées ») et le
 *     step-up à la volée (une mutation sensible reprend après ré-auth,
 *     aucune valeur perdue) ;
 *   · les ACTIONS ATOMIQUES (révoquer une session, supprimer une passkey,
 *     acquitter une alerte…) ne passent PAS par la barre : ce sont des
 *     boutons explicites avec confirmation — la barre ne sert que les
 *     modifications de valeurs.
 */

export interface SaveBarOptions {
  /** Écart RÉEL avec le snapshot serveur. */
  dirty: boolean
  /** Nombre de modifications (affiché : « 3 modifications non enregistrées »). */
  count?: number
  /** Verbe contextualisé : « Enregistrer » (défaut), « Créer le compte »… */
  saveLabel?: string
  /** Raison pour laquelle Enregistrer est désactivé (validation bloquante). */
  invalid?: string | null
  /** Effectue la sauvegarde. `true` = succès ; string = message d'erreur. */
  onSave: () => Promise<true | string>
  /** Restaure le snapshot serveur, sans requête d'écriture. */
  onCancel: () => void
  /** Toast de succès (défaut : « Modifications enregistrées »). */
  successMessage?: string
}

interface EditorEntry extends SaveBarOptions { id: number }

interface EditingApi {
  register: (entry: EditorEntry) => void
  unregister: (id: number) => void
}

const EditingCtx = createContext<EditingApi | null>(null)
let nextEditorId = 1

/** Une page/modale éditable déclare son unité d'édition. La Save Bar
 *  apparaît dès que `dirty` est vrai. */
export function useSaveBar(opts: SaveBarOptions): void {
  const api = useContext(EditingCtx)
  const idRef = useRef(0)
  if (idRef.current === 0) idRef.current = nextEditorId++
  const id = idRef.current

  // Ré-enregistre à chaque rendu : le provider a toujours la version fraîche
  // de dirty / onSave / onCancel (pas de closures périmées).
  useEffect(() => {
    api?.register({ id, ...opts })
  })
  useEffect(() => () => { api?.unregister(id) }, [api, id])
}

/* ─── module bus : fermeture gardée pour les surfaces (modales) ───
   `guardedClose(dirty, close)` : si rien n'est modifié, ferme ; sinon ouvre
   la modale « Modifications non enregistrées » et ferme selon la décision.
   `texte` (facultatif) remplace l'explication par défaut (« Si vous quittez
   cette page… ») quand l'action gardée ne QUITTE pas la page — par exemple
   un changement de statut qui fermera la fiche en cours d'édition. */
let guardImpl: ((proceed: () => void, texte?: string) => void) | null = null
export function guardedClose(dirty: boolean, close: () => void, texte?: string): void {
  if (!dirty || !guardImpl) { close(); return }
  guardImpl(close, texte)
}

/* ─── sonde dirty globale : la déconnexion (et toute action qui quitte la
   page) vérifie d'abord qu'aucune édition n'est en cours. ─── */
let dirtyProbe: () => boolean = () => false
export function isEditorDirty(): boolean { return dirtyProbe() }

/* ═══════════════════════════ PROVIDER ═══════════════════════════ */

export function EditingProvider(props: { children: ReactNode }) {
  const toast = useToast()
  const [editor, setEditor] = useState<EditorEntry | null>(null)
  const editorRef = useRef<EditorEntry | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null)
  const [pendingText, setPendingText] = useState<string | null>(null)
  const [stepUp, setStepUp] = useState<((ok: boolean) => void) | null>(null)

  const api = useRef<EditingApi>({
    register: (entry) => {
      const prev = editorRef.current
      editorRef.current = entry
      // Les fonctions changent d'identité à chaque rendu : on ne re-rend la
      // barre que si un champ AFFICHÉ change (sinon boucle infinie).
      if (!prev || prev.id !== entry.id || prev.dirty !== entry.dirty
        || prev.count !== entry.count || prev.saveLabel !== entry.saveLabel
        || prev.invalid !== entry.invalid) {
        setEditor(entry)
      }
    },
    unregister: (id) => {
      if (editorRef.current?.id === id) {
        editorRef.current = null
        setEditor(null)
        setError(null)
      }
    },
  })

  const doSave = useCallback(async (): Promise<boolean> => {
    const ed = editorRef.current
    if (!ed || savingRef.current || !ed.dirty || ed.invalid) return false
    savingRef.current = true
    setSaving(true)
    setError(null)
    try {
      const r = await ed.onSave()
      if (r === true) {
        toast('ok', ed.successMessage ?? 'Modifications enregistrées')
        return true
      }
      // Échec : la barre RESTE ouverte, les valeurs restent, on dit pourquoi.
      setError(r)
      return false
    } catch {
      setError('Impossible d’enregistrer les modifications — le serveur n’a pas répondu.')
      return false
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }, [toast])

  const doCancel = useCallback(() => {
    editorRef.current?.onCancel()
    setError(null)
  }, [])

  /* ─── garde de navigation : rien ne se perd silencieusement ─── */
  useEffect(() => {
    setNavigationGuard((to) => {
      if (!editorRef.current?.dirty) return true
      setPendingText(null)
      setPendingNav(() => () => navigate(to, { force: true }))
      return false
    })
    guardImpl = (proceed, texte) => { setPendingText(texte ?? null); setPendingNav(() => proceed) }
    dirtyProbe = () => !!editorRef.current?.dirty
    return () => { setNavigationGuard(null); guardImpl = null; dirtyProbe = () => false }
  }, [])

  /* ─── beforeunload : refresh / fermeture d'onglet avec du dirty ─── */
  useEffect(() => {
    const onBefore = (e: BeforeUnloadEvent) => {
      if (!editorRef.current?.dirty) return
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onBefore)
    return () => window.removeEventListener('beforeunload', onBefore)
  }, [])

  /* ─── Ctrl/Cmd+S = Enregistrer (jamais le dialogue du navigateur) ─── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        if (editorRef.current?.dirty) void doSave()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [doSave])

  /* ─── step-up à la volée (TOTP ou clé d'accès) ─── */
  useEffect(() => {
    setStepUpRequester(() => new Promise<boolean>((resolve) => {
      setStepUp(() => (ok: boolean) => { setStepUp(null); resolve(ok) })
    }))
    return () => setStepUpRequester(null)
  }, [])

  const active = editor !== null && editor.dirty

  return (
    <EditingCtx.Provider value={api.current}>
      {props.children}

      <SaveBar
        visible={active}
        count={editor?.count ?? 0}
        saveLabel={editor?.saveLabel ?? 'Enregistrer'}
        invalid={editor?.invalid ?? null}
        saving={saving}
        error={error}
        onSave={() => void doSave()}
        onCancel={doCancel}
      />

      <UnsavedDialog
        open={pendingNav !== null}
        text={pendingText}
        canSave={!editor?.invalid}
        onStay={() => setPendingNav(null)}
        onDiscard={() => {
          const proceed = pendingNav
          doCancel()
          setPendingNav(null)
          proceed?.()
        }}
        onSaveAndGo={() => {
          const proceed = pendingNav
          setPendingNav(null)
          void doSave().then((ok) => { if (ok) proceed?.() })
        }}
      />

      <StepUpDialog open={stepUp !== null} onDone={(ok) => stepUp?.(ok)} />
    </EditingCtx.Provider>
  )
}

/* ═══════════════════════════ SAVE BAR ═══════════════════════════ */

function SaveBar(props: {
  visible: boolean
  count: number
  saveLabel: string
  invalid: string | null
  saving: boolean
  error: string | null
  onSave: () => void
  onCancel: () => void
}) {
  // Sortie animée : la barre reste montée le temps de son fondu.
  const [mounted, setMounted] = useState(false)
  const [leaving, setLeaving] = useState(false)
  useEffect(() => {
    if (props.visible) { setMounted(true); setLeaving(false); return }
    if (!mounted) return
    setLeaving(true)
    const t = setTimeout(() => { setMounted(false); setLeaving(false) }, 220)
    return () => clearTimeout(t)
  }, [props.visible]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!mounted) return null
  const label = props.count > 1
    ? `${props.count} modifications non enregistrées`
    : 'Modifications non enregistrées'

  return createPortal(
    <div className={`lc-savebar-zone${leaving ? ' lc-savebar-zone--leaving' : ''}`} aria-hidden={leaving ? 'true' : undefined}>
      <div className="lc-savebar" role="status" aria-live="polite">
        <span className="lc-savebar__pulse" aria-hidden="true" />
        <span className="lc-savebar__text">
          <span className="lc-savebar__title">{label}</span>
          {props.error ? (
            <span className="lc-savebar__error" role="alert"><AlertCircle size={13} /> {props.error}</span>
          ) : props.invalid ? (
            <span className="lc-savebar__hint">{props.invalid}</span>
          ) : null}
        </span>
        <span className="lc-savebar__actions">
          <button className="lc-btn lc-btn--ghost" onClick={props.onCancel} disabled={props.saving}>
            Annuler
          </button>
          <button
            className="lc-btn lc-btn--primary lc-savebar__cta"
            onClick={props.onSave}
            disabled={props.saving || props.invalid !== null}
          >
            {props.saving ? <span className="lc-spin" aria-hidden="true" /> : null}
            {props.saving ? 'Enregistrement…' : props.saveLabel}
          </button>
        </span>
      </div>
    </div>,
    document.body,
  )
}

/* ═══════════════ MODALE « MODIFICATIONS NON ENREGISTRÉES » ═══════════════ */

function UnsavedDialog(props: {
  open: boolean
  /** Remplace l'explication par défaut (navigation) : voir `guardedClose`. */
  text: string | null
  canSave: boolean
  onStay: () => void
  onDiscard: () => void
  onSaveAndGo: () => void
}) {
  useOverlay(props.open, props.onStay, { lockScroll: true })
  const stayRef = useRef<HTMLButtonElement>(null)
  const presence = usePresence(props.open, 200)
  useEffect(() => { if (props.open) stayRef.current?.focus() }, [props.open])
  if (!presence.mounted) return null

  return createPortal(
    <div className="lc-dialog-overlay" data-state={presence.state} role="presentation" onClick={props.onStay}>
      <div className="lc-dialog" data-state={presence.state} role="alertdialog" aria-modal="true" aria-labelledby="unsaved-title" onClick={(e) => e.stopPropagation()}>
        <span className="lc-dialog__edge" aria-hidden="true" />
        <h2 className="lc-dialog__title" id="unsaved-title">Modifications non enregistrées</h2>
        <div className="lc-dialog__text">
          {props.text ?? (
            <>
              Vous avez des changements qui n’ont pas été enregistrés. Si vous
              quittez cette page maintenant sans enregistrer, ils seront perdus.
            </>
          )}
        </div>
        <div className="lc-dialog__actions lc-dialog__actions--stack">
          <button ref={stayRef} type="button" className="lc-btn lc-btn--primary" onClick={props.onStay}>
            Continuer à modifier
          </button>
          {props.canSave ? (
            <button type="button" className="lc-btn" onClick={props.onSaveAndGo}>
              Enregistrer et continuer
            </button>
          ) : null}
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--danger-text" onClick={props.onDiscard}>
            Abandonner les modifications
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/* ═══════════════════════ STEP-UP (TOTP ou passkey) ═══════════════════════ */

function StepUpDialog(props: { open: boolean; onDone: (ok: boolean) => void }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [hasPasskey, setHasPasskey] = useState(false)
  useOverlay(props.open, () => props.onDone(false), { lockScroll: true })
  const presence = usePresence(props.open, 200)

  useEffect(() => {
    if (!props.open) return
    setCode(''); setErr(null); setBusy(false)
    // A-t-il une clé d'accès ? (fait sur son propre compte, rien de sensible)
    fetch('/api/v1/auth/webauthn/credentials', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { credentials: [] }))
      .then((j: { credentials?: unknown[] }) => setHasPasskey((j.credentials ?? []).length > 0))
      .catch(() => setHasPasskey(false))
  }, [props.open])

  if (!presence.mounted) return null

  const submitTotp = async () => {
    if (busy || code.length < 6) return
    setBusy(true)
    const r = await apiMutate<{ ok: boolean }>('POST', '/api/v1/auth/step-up', { code })
    setBusy(false)
    if (r.status === 200) props.onDone(true)
    else { setErr('Code refusé — réessayez.'); setCode('') }
  }

  const submitPasskey = async () => {
    if (busy) return
    setBusy(true)
    setErr(null)
    try {
      const opts = await apiMutate<{ options?: Record<string, unknown>; error?: string }>('POST', '/api/v1/auth/webauthn/step-up/options')
      if (opts.status !== 200 || !opts.data.options) throw new Error('options')
      const assertion = await startAuthentication({ optionsJSON: opts.data.options as never })
      const v = await apiMutate<{ ok: boolean }>('POST', '/api/v1/auth/webauthn/step-up/verify', { response: assertion })
      if (v.status === 200) { props.onDone(true); return }
      setErr('Clé d’accès refusée — réessayez ou utilisez un code TOTP.')
    } catch {
      setErr('La vérification par clé d’accès a été interrompue.')
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="lc-dialog-overlay lc-dialog-overlay--stepup" data-state={presence.state} role="presentation" onClick={() => props.onDone(false)}>
      <div className="lc-dialog lc-stepup" data-state={presence.state} role="dialog" aria-modal="true" aria-labelledby="stepup-title" onClick={(e) => e.stopPropagation()}>
        <span className="lc-dialog__edge" aria-hidden="true" />
        <h2 className="lc-dialog__title" id="stepup-title">
          <ShieldCheck size={17} style={{ verticalAlign: -2, marginRight: 8 }} />
          Confirmation forte requise
        </h2>
        <div className="lc-dialog__text">
          Cette opération est sensible : confirmez votre identité pour
          continuer. Vos modifications sont conservées.
        </div>
        {err ? <p className="lc-form__error" role="alert">{err}</p> : null}
        {hasPasskey ? (
          <button className="lc-btn lc-stepup__passkey" onClick={() => void submitPasskey()} disabled={busy}>
            <Fingerprint size={16} /> Utiliser ma clé d’accès
          </button>
        ) : null}
        <label className="lc-label" htmlFor="stepup-code" style={{ marginTop: hasPasskey ? 10 : 0 }}>
          {hasPasskey ? 'Ou saisissez un code de votre application' : 'Code de votre application d’authentification'}
        </label>
        <input
          id="stepup-code" className="lc-input" inputMode="numeric" autoComplete="one-time-code"
          maxLength={6} value={code} autoFocus={!hasPasskey}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') void submitTotp() }}
          aria-label="Code TOTP"
        />
        <div className="lc-dialog__actions">
          <button className="lc-btn lc-btn--ghost" onClick={() => props.onDone(false)}>Annuler</button>
          <button className="lc-btn lc-btn--primary" disabled={busy || code.length < 6} onClick={() => void submitTotp()}>
            Confirmer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
